import { isEmpty, isNull } from "lodash";
import i18n from "i18next";
import { SortOrder, InputData, IOutputData } from "../types/types";

import { urlConfiguration } from "../config/urlConfiguration";
import axiosInstance from "../config/interceptors/axios.interceptor";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/hooks";
import { setInitialUserDetails } from "../redux/slices/userDetailsSlice";
import { saveStateToSessionStorage } from "./sessionStorageUtility";
import { RootState } from "../redux/store";
import { ACTIONS, QTypes } from "../constants/dataConstants";
import authService from "../config/auth.service";
import {
  useGetHatvNotesListQuery,
  useGetHatvAwardNotesListQuery,
} from "../redux/services/hatvQMonthNotesAPI";

// Get initials of passed string
export const getInitials = (fullName: String) => {
  const words = fullName?.split(" ");
  let initials = "";
  for (let i = 0; i < words?.length && i < 3; i++) {
    initials += words[i].replace(/[^a-zA-Zก-๙]/g, "").charAt(0);
  }
  return initials;
};

// convert utc date to format like [12-04-22 3:30pm]
export const formatDate = (date: string) => {
  // Extracting components from the date
  const newDate = new Date(date);
  const day = newDate.getDate()?.toString()?.padStart(2, "0");
  const month = (newDate.getMonth() + 1)?.toString()?.padStart(2, "0");
  const year = newDate?.getFullYear()?.toString()?.slice(-2);

  const minutes = newDate?.getMinutes()?.toString().padStart(2, "0");
  const hours = newDate.getHours() % 12 || 12; // Convert to 12-hour format
  const ampm = newDate.getHours() >= 12 ? "pm" : "am";

  // Constructing the formatted string
  const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}${ampm}`;

  return formattedDate;
};
export function compareValues(
  valueA: string | number,
  valueB: string | number,
  options?: { numeric?: boolean }
): number {
  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueA - valueB;
  }

  const stringA = String(valueA ?? "")
    .toLowerCase()
    .trim();
  const stringB = String(valueB ?? "")
    .toLowerCase()
    .trim();

  // When numeric is true, compare strings with numeric awareness (e.g., "A2" < "A10")
  if (options?.numeric) {
    return stringA.localeCompare(stringB, undefined, { numeric: true });
  }
  // Default behavior to avoid regressions
  return stringA.localeCompare(stringB);
}

export const sortTableData = <T>(
  data: T[] | undefined,
  column: keyof T,
  order: SortOrder = "desc"
): T[] => {
  if (!data || data.length === 0) {
    return [];
  }

  if (!["asc", "desc"].includes(order)) {
    console.log("Order parameter must be 'asc' or 'desc'");
    return data;
  }

  if (!data[0]?.hasOwnProperty(column)) {
    console.error("Column not found in data");
  }

  return data?.sort((a, b) => {
    const valueA: any = a[column];
    const valueB: any = b[column];

    // Use the comparison function to compare values
    if (order === "asc") {
      return compareValues(valueA, valueB);
    } else {
      return compareValues(valueB, valueA); // Reverse order for descending
    }
  });
};

function flattenObject(obj: any, keysToFind: string[]): any {
  let results: any = null;

  function findHelper(obj: any) {
    for (const key in obj) {
      if (keysToFind?.includes(key)) {
        const result = { [key]: obj[key] };
        results = { ...results, ...result };
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        findHelper(obj[key]);
      }
    }
  }

  findHelper(obj);
  return results;
}

export function findKeysInArray(arr: any[], keysToFind: string[]): any[] {
  const newArr: any = [];
  arr?.length > 0 &&
    arr?.map((obj: any) => {
      if (!isNull(obj)) {
        const flattenObj = flattenObject(obj, keysToFind);
        newArr?.push(flattenObj);
      }
    });
  return newArr;
}

//--- Account Table Utility function

export function getNestedValue<T>(obj: T, path: string): any {
  return path
    ?.split(".")
    ?.reduce((acc: any, part: string) => acc && acc[part], obj);
}

export function sortAccountTableData<T>(
  data: T[],
  path: string,
  order: SortOrder
): T[] {
  // Check if the sort order is 'nosort'
  if (order === "nosort" || isEmpty(data)) {
    return data;
  }

  // Check if the column exists in the first row of the data
  if (data?.length > 0 && getNestedValue(data[0], path) === undefined) {
    console.warn(`Column path "${path}" does not exist in the data.`);
    return data;
  }

  let arrayForSort = [...data];
  return arrayForSort?.sort((a, b) => {
    const valueA: any = getNestedValue(a, path);
    const valueB: any = getNestedValue(b, path);

    if (order === "asc") {
      return compareValues(valueA, valueB);
    } else {
      return compareValues(valueB, valueA); // Reverse order for descending
    }
  });
}

// US phone number format
export const formatPhoneNumber = (
  phoneNumber: string | number | undefined,
  phoneId: string | number | undefined,
  phoneArea: string | number | undefined,
  phoneExtension: string | number | undefined
) => {
  // If empty string - return
  if (!phoneNumber) {
    return phoneNumber;
  }
  // Remove all non-digit characters
  const cleaned: any = ("" + phoneArea + phoneNumber).replace(/\D/g, "");
  // Check if the input is of correct length
  if (cleaned?.length !== 10) {
    console.error("Invalid phone number length. Expected 10 digits.");
  }
  // Format the number
  const formattedPhoneNumber: any = `${phoneId ? "+" + phoneId : ""}${phoneId == "1" && cleaned?.length === 10
      ? ` ${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
      : `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
    }`; // Add space between sliced if needed-AMW-460

  const cleanedExtension = ("" + phoneExtension).replace(/\D/g, "");
  const formattedPhoneNumberWithExtension =
    formattedPhoneNumber + (cleanedExtension ? ", " + cleanedExtension : "");

  return formattedPhoneNumberWithExtension;
};

export const formatUSPostalCode = (
  postalCode: string | number | undefined,
  primaryAddressCountry: string | undefined
) => {
  // If empty string - return
  if (!postalCode) {
    return postalCode || "";
  }
  // Remove all non-digit characters
  const cleaned: any = ("" + postalCode).replace(/\D/g, "");
  // Check if the input is of correct length
  if (cleaned?.length !== 9) {
    console.error("Invalid postal code length. Expected 9 digits.");
  }
  // Format the postal code
  const formatted = `${cleaned?.length > 5 && primaryAddressCountry?.toLowerCase() === "us" ? cleaned?.slice(0, 5) + "-" + cleaned?.slice(5) : cleaned}`;
  return formatted;
};

// Determine translation keys based on screenName
export const translateKey = (key: string, screenName?: string) => {
  switch (screenName) {
    case "apInformation":
      return `AP_info.${key}`;
    default:
      return key;
  }
};

export const downloadDocument = async (type: string) => {
  const URL = `${urlConfiguration.baseUrl}/admin/v2/document/type/${type}`;
  try {
    const response = await axiosInstance.get(URL);
    //const dataBlob = new Blob([response.data], { type: "application/pdf" });
    //const url = window.URL.createObjectURL(dataBlob);
    const url = response?.data?.body?.path;
    if (type === "playbook" || type === "playbook-hatv") {
      window.open(url, "_blank");
      return;
    }
    //OPEN in new tab
    // if (isDocType === 'pdf') {
    //   window.open(url, "_blank");
    //   return;
    // }
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.setAttribute("download", `${"Playbook"}`);
    document.body.appendChild(tempLink);
    tempLink.click();

    // Clean up the temporary elements and URL
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading Playbook Docs:", error);
  }
};

export const transformMyAccountAPIRespData = async (
  inputData: InputData
): Promise<IOutputData> => {
  try {
    // Simulating async processing (Replace with actual API call if needed)
    return Promise.resolve(transformData(inputData));
  } catch (error) {
    console.error("Error transforming MyAccount V2 API data:", error);
    throw error;
  }
};
export const transformData = (data: InputData): IOutputData => {
  return {
    abos: data?.abos?.map((aboKPI) => {
      const transformedKpis: Record<string, any> = {
        aboNumber: aboKPI.aboNumber,
        name: aboKPI.name,
        affiliateCode: aboKPI.affiliateCode,
        localName: aboKPI.localName,
        marketCountryCode: aboKPI.marketCountryCode,
        marketName: aboKPI.marketName,
        currentAward: aboKPI.currentAward,
        trackingAward: aboKPI.trackingAward,
        primaryAccountManager: aboKPI.primaryAccountManager,
      };
      if (aboKPI.kpis) {
        aboKPI?.kpis?.forEach((kpi) => {
          transformedKpis[kpi.kpiCode] = kpi;
        });
      }

      return transformedKpis;
    }),
    closingMonth: data?.closingMonth,
    pagination: data?.pagination,
  };
};

export const convertUrlsToLinks = (
  content: string,
  theme: "light" | "dark"
): string => {
  if (!content) return content;

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
      const text = node.nodeValue || "";

      if (urlRegex.test(text)) {
        // Replace URLs with anchor tags
        const newHtml = text.replace(urlRegex, (url) => {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="custom-text">${url}</a>`;
        });

        // Create a span wrapper
        const wrapper = document.createElement("span");
        wrapper.innerHTML = newHtml;

        // Replace text node with multiple nodes (safe alternative to replaceWith)
        if (node.parentNode) {
          node.parentNode.replaceChild(wrapper, node);
        }
      }
    } else if (
      node.nodeType === Node.ELEMENT_NODE &&
      (node as HTMLElement).tagName !== "A"
    ) {
      // Convert NodeList to an array to avoid iteration issues
      Array.from(node.childNodes).forEach(processNode);
    }
  };

  // Convert NodeList to array to avoid downlevel iteration issues
  Array.from(tempDiv.childNodes).forEach(processNode);

  // Now apply the inline style to anchor tags inside the div if needed
  const anchors = tempDiv.querySelectorAll("a");
  anchors.forEach((anchor) => {
    const linkColor = theme === "light" ? "#5969D6" : "#99B2F5";
    anchor.style.color = linkColor;
    anchor.style.textDecoration = "underline";
  });

  return tempDiv.innerHTML;
};

export const useProfileParams = () => {
  const params = useParams<{
    tabId?: string;
    affiliateCode?: string;
    aboNumber?: string;
    role?: string;
    type?: string;
  }>();

  return {
    tabId: params.tabId || "",
    affiliateCode: params.affiliateCode || "",
    aboNumber: Number(params.aboNumber) || 0,
    role: params.role || "",
    type: params?.type || "",
  };
};

export const formatDatetoLong = (dateStr: string): string => {
  if (!dateStr) return "-";
  if (/^\d{6}$/.test(dateStr)) {
    const year = parseInt(dateStr.slice(0, 4), 10);
    const month = parseInt(dateStr.slice(4, 6), 10);

    const date = new Date(year, month - 1);
    const monthLong = date.toLocaleString("default", { month: "long" });
    return `${monthLong} ${year}`; // e.g. April 2025
  }

  let date: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateStr);
  }
  if (isNaN(date.getTime())) return "-";

  const day = date.getDate();
  const monthShort = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  return `${monthShort} ${day}, ${year}`;
};

export const getDescriptionFromCode = (code: string) => {
  switch (code) {
    case "gar_tracking_report":
      return "gar_desc";
    case "als_tracking_report":
      return "als_desc";
    default:
      return "";
  }
};
export const getTitleFromCode = (code: string) => {
  switch (code) {
    case "gar_tracking_report":
      return "gar_title";
    case "als_tracking_report":
      return "als_title";
    default:
      return "";
  }
};

export const getFullMonthYear = (code: string): string => {
  if (!code || code.length < 6) return "";
  const year = code.slice(0, 4);
  const month = parseInt(code.slice(4), 10);
  const date = new Date(Number(year), month - 1);
  return date.toLocaleString(i18n.language, { year: "numeric", month: "long" });
};

export const getShortMonthYear = (code: string): string => {
  const year = code.slice(0, 4);
  const month = parseInt(code.slice(4), 10);
  const date = new Date(Number(year), month - 1);
  return date.toLocaleString(i18n.language, {
    year: "numeric",
    month: "short",
  });
};

export const getYear = (code: string): string => {
  return code.slice(0, 4);
};

export const getShortYear = (code: string): string => {
  return code.slice(2, 4);
};

export const getUniqueYears = (
  codes: string[]
): { year: string; code: string }[] => {
  const yearMap = codes.reduce((map, code) => {
    const year = code.slice(0, 4);
    if (!map.has(year)) {
      map.set(year, code);
    }
    return map;
  }, new Map<string, string>());

  return Array.from(yearMap, ([year, code]) => ({ year, code }));
};

export const getSlashSeparatedFormattedDate = (dateParam: string) => {
  if (!dateParam) return "";
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateParam);

  let date: Date;
  if (isDateOnly) {
    const [year, month, day] = dateParam.split("-").map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateParam);
  }

  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formattedDate;
};

export const formatDateShort = (date: Date | string) => {
  if (!date) return "";

  let newDate: Date;

  if (typeof date === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      newDate = new Date(year, month - 1, day);
    } else {
      newDate = new Date(date);
    }
  } else {
    newDate = date;
  }

  if (isNaN(newDate.getTime())) {
    return "-";
  }

  const dayStr = newDate.getDate().toString().padStart(2, "0");
  const monthStr = newDate.toLocaleString("en-GB", { month: "short" });
  const yearStr = newDate.getFullYear();

  return `${dayStr} ${monthStr}, ${yearStr}`; // e.g. "17 Oct, 2025"
};

export function getTimePeriodOptionsTillCurrent(startMonth: number) {
  const options = [];
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based (0 = Jan)

  const startYear = currentMonth >= startMonth ? currentYear : currentYear - 1;

  let year = startYear;
  let month = startMonth;

  while (year < currentYear || (year === currentYear && month < currentMonth)) {
    const label = `${monthNames[month]} ${year}`;
    const value = year * 100 + month + 1;

    options.push({ label, value });

    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }

  return options;
}
export const getCustomShortMonthLabel = (code?: string): string => {
  if (!code || code.length < 6) return "";
  const customShortMonths = [
    "Jan",
    "Feb",
    "March",
    "April",
    "May",
    "June",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = parseInt(code.slice(4), 10) - 1;
  return customShortMonths[monthIndex] || "";
};
// Use this common sort for HATV table
export function sortTableListData<T>(
  data: T[],
  path: string,
  order: SortOrder = "nosort",
  numeric?: boolean
): T[] {
  if (!Array.isArray(data) || data.length === 0 || order === "nosort") {
    return data;
  }

  return [...data].sort((a, b) => {
    const valueA = getNestedValue(a, path);
    const valueB = getNestedValue(b, path);

    const useNumeric =
      numeric || (typeof valueA === "number" && typeof valueB === "number");

    return order === "asc"
      ? compareValues(valueA, valueB, { numeric: useNumeric })
      : compareValues(valueB, valueA, { numeric: useNumeric });
  });
}

export function useABOProfileNavigation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useAppSelector(
    (state: RootState) => state.userDetails.user
  );

  const handleABOInfoClick = (
    aboNumber?: string,
    affiliateCode?: string,
    tabId?: string
  ): void => {
    if (!aboNumber || !affiliateCode) return;
    const defaultTabId =
      tabId || localStorage.getItem("selectedLink") || "businessperformance";
    dispatch(
      setInitialUserDetails({
        userDetails: { ...userDetails, affiliateCode, aboNumber },
      })
    );
    saveStateToSessionStorage({ affiliateCode, aboNumber });
    navigate(`/profile/${defaultTabId}/${affiliateCode}/${aboNumber}`);
  };

  return handleABOInfoClick;
}

export function goalModalReducer(
  state: OperationState,
  action: OperationAction
) {
  switch (action.type) {
    case ACTIONS.ADD_ACTIONS:
    case ACTIONS.EDIT:
    case ACTIONS.ADD_EDIT_METRICS:
    case ACTIONS.DELETE:
    case ACTIONS.DELETE_METRICS:
      return { type: action.type, payload: action.payload };
    default:
      return state;
  }
}

export const getLoggedUserId = () => {
  const token = localStorage.getItem("auth-accessToken") || "";
  let loggedUserId = "";
  const decodedAccessToken: any = token && authService.decodeAccessToken(token);
  if (decodedAccessToken) {
    loggedUserId = decodedAccessToken["NativeId"];
  }

  return loggedUserId;
};

export const isOwner = (owner?: string): boolean => getLoggedUserId() === owner;

export const isSpecialQType = (value: string | undefined | null): boolean => {
  if (!value) return false;
  const v = value.trim().toUpperCase();
  return v === QTypes.Q1 || v === QTypes.Q2;
};
export const getLanguageName = (code: string) => {
  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    return displayNames.of(code) ?? code; // fallback to code if not recognized
  } catch {
    return code; // or return a default like 'Unknown'
  }
};

export const getHatvNotesQueryHook = (apiType: "qMonth" | "awardApproval") => {
  if (apiType === "qMonth") {
    return { useGetHatvNotes: useGetHatvNotesListQuery };
  } else if (apiType === "awardApproval") {
    return { useGetHatvNotes: useGetHatvAwardNotesListQuery };
  } else {
    throw new Error("Unsupported API type");
  }
};

export const truncateValue = (
  val: number | string | null | undefined
): string | undefined => {
  if (val === null || val === undefined || val === "") return undefined;

  const num = Number(val);
  if (Number.isNaN(num)) return val.toString();
  const truncated = num < 0 ? Math.ceil(num) : Math.floor(num);
  return truncated.toString();
};

export const getYearFromAward = (selectedYear: string) => {
  if (!selectedYear) return "";
  const numericYear = parseInt(selectedYear);
  return `${numericYear - 1}${"09"}`;
};
export const getYearFromQMonth = (selectedMonthYear: string | null) => {
  if (!selectedMonthYear) return "";

  const year = parseInt(selectedMonthYear.substring(0, 4));
  const month = parseInt(selectedMonthYear.substring(4, 6));

  if (month >= 9) {
    return `${year + 1}`;
  } else {
    return `${year}`;
  }
};
// Function to Validate lineChartData has valid structure with lines and yAxis
export const isValidLineChartData = (lineChartData: any): boolean => {
  return (
    lineChartData &&
    lineChartData?.body?.lines &&
    Array.isArray(lineChartData.body.lines) &&
    lineChartData.body.lines.length > 0 &&
    lineChartData?.yAxis &&
    Array.isArray(lineChartData.yAxis) &&
    lineChartData.yAxis.length > 0
  );
};

export const valueText = (val: any, kpi: any) => {
  // if(!val) return '';

  const output = Number.isFinite(val)
    ? val.toLocaleString('en-US')
    : String(val ?? '');

  return kpi?.kpiDataType === "currency" &&  kpi?.kpiCurrency === "USD" ? `$${output}` : output;
}

export const getFromLocalStorage = <T>(key: string): T | null => {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
};